import { useEffect } from 'react'
import { useChats } from './api'

/**
 * Mirror the total unread count to:
 *
 *   1. `navigator.setAppBadge(n)` — Chromium's Web Badging API. On
 *      Android Chrome PWAs this paints a numeric red dot on the
 *      home-screen icon (and the launcher icon counter), exactly like
 *      WhatsApp / Telegram. Falls back to silently no-op on browsers
 *      that don't implement the API.
 *
 *   2. `document.title` — `(3) AgentChat` while there are unread
 *      messages, just `AgentChat` otherwise. Visible in the browser
 *      tab, the Android task switcher, and macOS dock peek.
 *
 * Manual chats contribute unread messages. Auto-spawn groups contribute
 * active unread topics, so one noisy thread cannot inflate the app badge and
 * snoozed/Done topics do not demand current attention. The
 * hook re-runs whenever the chats query refetches (live via WS
 * invalidations + 30s safety net), so the badge tracks reality with
 * low lag.
 *
 * Mount this once at the App root so it stays alive for every route.
 */
export function useAppUnreadBadge(enabled = true) {
  const { data: chats } = useChats(enabled)
  const total = (chats ?? []).reduce((sum, chat) => (
    sum + (chat.type === 'auto-spawn'
      ? (chat.active_unread_topic_count ?? 0)
      : (chat.unread ?? 0))
  ), 0)

  useEffect(() => {
    // 1. Home-screen badge (Android Chrome / desktop Chrome).
    if (typeof navigator !== 'undefined') {
      const nav = navigator as Navigator & {
        setAppBadge?: (count?: number) => Promise<void>
        clearAppBadge?: () => Promise<void>
      }
      if (total > 0) {
        nav.setAppBadge?.(total).catch(() => {/* unsupported */})
      } else {
        nav.clearAppBadge?.().catch(() => {/* unsupported */})
      }
    }

    // 2. Tab title prefix.
    if (typeof document !== 'undefined') {
      const base = 'AgentChat'
      document.title = total > 0 ? `(${total}) ${base}` : base
    }
  }, [total])
}
